import { createQueries } from '@/entities/create';
import {
    publishQueries,
    usePublishedBoxDetailStore,
    usePublishedClassMembers,
} from '@/entities/sets/publish';
import { usePersistStore } from '@/shared/lib/store';
import { useModalStore } from '@/shared/lib/store/model/modal.store';
import { ErrorPopup, InputToggle, Modal, TabMenu } from '@/shared/ui';
import { getCountArticleList, getDifficultyArticleList } from '@/shared/utils/created';
import {
    PubDataInfoHeader,
    PubDetailHeader,
    PubPublishedData,
    PubStntSubList,
} from '@/widgets/published-box';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

export const Route = createFileRoute('/sets/published-box/detail/$publishId')({
    component: RouteComponent,
    loader: ({ params }) => {
        return { publishId: Number(params.publishId) };
    },
});

function RouteComponent() {
    const router = useRouter();
    const queryClient = useQueryClient();
    const { publishId } = Route.useLoaderData();
    const { showModal } = useModalStore();

    const [tab, setTab] = useState<string>('학생 제출 현황');
    const legacyTab = usePublishedBoxDetailStore((state) => state.legacyTab);
    const setLegacyTab = usePublishedBoxDetailStore((state) => state.setLegacyTab);

    const [orderBy, setOrderBy] = useState('1'); // 1. 제출 시간 2. 점수 3. 가나다
    const { sUserId, sToken } = usePersistStore();
    const [isModalOpen, setIsModalOpen] = useState(false);

    const { mutate } = publishQueries.detail.postPubUpdateHint();

    const { data, isFetched } = publishQueries.detail.getPublishDetail(
        { publishId: publishId, wrterId: sUserId },
        !!sToken
    );
    const [hintState, setHintState] = useState<boolean>();

    usePublishedClassMembers(data?.publishInfoDetail?.publishType === 1);

    const { data: viewerData } = createQueries.detail.getDataDetailInfo(
        {
            setsId: data?.publishInfoDetail?.setsId
                ? data?.publishInfoDetail?.setsId?.toString()
                : '',
            userId: sUserId,
        },
        !!data && !!data?.publishInfoDetail?.setsId
    );

    const { data: stntData } = publishQueries.detail.getPublishSubStnt(
        {
            publishId: publishId,
            orderBy: orderBy,
        },
        !!data && data?.publishInfoDetail?.publishType !== 3
    );

    const articleList: DATA.ArticleInfo[] =
        getDifficultyArticleList(viewerData?.articleList as DATA.ArticleInfo[]) ?? [];

    const countList: DATA.ArticleInfo[] =
        getCountArticleList(viewerData?.articleList as DATA.ArticleInfo[]) ?? [];

    useEffect(() => {
        if (!legacyTab && data) {
            setHintState(data.publishInfoDetail?.hintOnOff === 'Y');
            if (data.publishInfoDetail?.publishType === 3) {
                setTab('발행한 자료');
            } else {
                setTab('학생 제출 현황');
            }
        }
    }, [data, legacyTab]);

    useEffect(() => {
        return () => {
            setLegacyTab(null);
            queryClient.removeQueries({
                queryKey: ['publish-detail', 'data-info', { publishId: publishId }],
            });
        };
    }, []);

    useEffect(() => {
        if (
            !!data?.resultMsg &&
            (data.resultMsg === '접근 권한이 없습니다' ||
                data.resultMsg === '발행 생성자와 조회 계정이 다릅니다')
        ) {
            setIsModalOpen(true);
        } else if (data?.resultCode === '0001' || (isFetched && data === undefined)) {
            showModal({
                isCustom: true,
                id: 'error',
                initContents: () => <ErrorPopup onClose={() => router.history.back()} />,
            });
        } else {
            setIsModalOpen(false);
        }
    }, [data, isFetched]);

    const onHintToggle = (value: boolean) => {
        setHintState(value);
        mutate({ publishId: publishId, hintOnOff: value ? 'Y' : 'N' });
    };

    return (
        <>
            <Modal
                open={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                    router.history.back();
                }}
                title="안내"
                description={
                    data?.resultCode === '0002' ? (
                        <>
                            접근 권한이 없습니다.
                            <br />
                            본인이 발행한 내역에만 접근할 수 있습니다.
                        </>
                    ) : (
                        data?.resultMsg
                    )
                }
            />
            <div className="contwrap">
                <div className="contInner">
                    <div className="published-detail published-detail__outer">
                        <PubDetailHeader data={data} />

                        <div className="published-detail__body">
                            <TabMenu
                                reset={true}
                                tabType={3}
                                tabItems={
                                    data?.publishInfoDetail?.publishType === 3
                                        ? [{ tabName: '발행한 자료', value: '발행한 자료' }]
                                        : [
                                              {
                                                  tabName: '학생 제출 현황',
                                                  value: '학생 제출 현황',
                                              },
                                              { tabName: '발행한 자료', value: '발행한 자료' },
                                          ]
                                }
                                selected={tab}
                                className="tab-size3"
                                onChange={(value: string) => {
                                    setTab(value);
                                    setLegacyTab(value);
                                }}
                            />
                            <div className="tab-contents__tab-type3">
                                <PubDataInfoHeader
                                    tab={tab}
                                    data={data}
                                    stntData={stntData}
                                    viewerData={viewerData}
                                    articleList={articleList}
                                    countList={countList}
                                    setOrderBy={setOrderBy}
                                />
                                <div className="published-info__body">
                                    {tab === '학생 제출 현황' && stntData && (
                                        <PubStntSubList
                                            addBtn={
                                                data?.publishInfoDetail?.publishType === 1 &&
                                                (data?.publishInfoDetail?.sttsNm === '예정' ||
                                                    data?.publishInfoDetail?.sttsNm === '진행중')
                                            }
                                            stntData={stntData}
                                            data={data}
                                            publishId={publishId}
                                            orderBy={orderBy}
                                        />
                                    )}
                                    {tab === '발행한 자료' && viewerData && (
                                        <>
                                            {data?.publishInfoDetail?.sttsNm == '예정' && (
                                                <div className="published-file__header">
                                                    <div className="pos-r">
                                                        <InputToggle
                                                            tgl_type="style1"
                                                            checked={hintState}
                                                            onChange={() =>
                                                                onHintToggle(!hintState)
                                                            }
                                                        >
                                                            <span className="hint-toggle">
                                                                학생 화면에 힌트 노출 on / off
                                                            </span>
                                                        </InputToggle>
                                                    </div>
                                                </div>
                                            )}
                                            <div className="published-file__body">
                                                <PubPublishedData
                                                    articleList={articleList}
                                                    setsId={
                                                        data?.publishInfoDetail?.setsId
                                                            ? data?.publishInfoDetail?.setsId?.toString()
                                                            : ''
                                                    }
                                                />
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
